import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Skeleton,
} from "@repo/ui";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import {
  getUpgradePreview,
  switchToAnnual,
  type UpgradePreview,
} from "@/lib/api/stripe";

interface UpgradePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function UpgradePlanDialog({
  open,
  onOpenChange,
  onSuccess,
}: UpgradePlanDialogProps) {
  const [preview, setPreview] = useState<UpgradePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setPreview(null);
      setError("");
      setUpgradeSuccess(false);
      return;
    }

    async function fetchPreview() {
      setIsLoadingPreview(true);
      setError("");
      try {
        const data = await getUpgradePreview();
        setPreview(data);
      } catch {
        setError("Failed to load upgrade details. Please try again.");
      } finally {
        setIsLoadingPreview(false);
      }
    }

    fetchPreview();
  }, [open]);

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    setError("");
    try {
      await switchToAnnual();
      setUpgradeSuccess(true);
      onSuccess();
    } catch {
      setError("Failed to upgrade plan. Please try again or contact support.");
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {upgradeSuccess ? (
          <SuccessView preview={preview} onClose={() => onOpenChange(false)} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-secondary" />
                Switch to Annual Billing
              </DialogTitle>
              <DialogDescription>
                Review the details below before confirming your plan change.
              </DialogDescription>
            </DialogHeader>

            {isLoadingPreview ? (
              <PreviewSkeleton />
            ) : error && !preview ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <AlertCircle className="h-8 w-8 text-destructive" />
                <p className="text-sm text-destructive text-center">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setError("");
                    setIsLoadingPreview(true);
                    getUpgradePreview()
                      .then(setPreview)
                      .catch(() =>
                        setError(
                          "Failed to load upgrade details. Please try again."
                        )
                      )
                      .finally(() => setIsLoadingPreview(false));
                  }}
                >
                  Retry
                </Button>
              </div>
            ) : preview ? (
              <PreviewContent
                preview={preview}
                error={error}
                isUpgrading={isUpgrading}
                onUpgrade={handleUpgrade}
                onCancel={() => onOpenChange(false)}
              />
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewSkeleton() {
  return (
    <div className="space-y-4 py-2">
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-32 rounded-xl" />
      <Skeleton className="h-10 rounded-lg" />
    </div>
  );
}

function PreviewContent({
  preview,
  error,
  isUpgrading,
  onUpgrade,
  onCancel,
}: {
  preview: UpgradePreview;
  error: string;
  isUpgrading: boolean;
  onUpgrade: () => void;
  onCancel: () => void;
}) {
  const { currency } = preview;
  const monthlyEquivalent = Math.round(preview.newPlan.amount / 12);

  return (
    <>
      <div className="space-y-4">
        {/* Plan comparison */}
        <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-3">
          <div className="rounded-xl border p-3 text-center">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Current
            </p>
            <p className="text-sm font-semibold mt-1">Monthly</p>
            <p className="text-lg font-bold text-foreground">
              {formatAmount(preview.currentPlan.amount, currency)}
              <span className="text-xs font-normal text-muted-foreground">
                /mo
              </span>
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <div className="rounded-xl border-2 border-secondary/40 bg-secondary-soft p-3 text-center">
            <p className="text-xs font-medium text-secondary uppercase tracking-wide">
              New
            </p>
            <p className="text-sm font-semibold mt-1">Annual</p>
            <p className="text-lg font-bold text-foreground">
              {formatAmount(monthlyEquivalent, currency)}
              <span className="text-xs font-normal text-muted-foreground">
                /mo
              </span>
            </p>
          </div>
        </div>

        {/* Billing breakdown */}
        <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Billing Summary
          </p>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Annual plan</span>
              <span>{formatAmount(preview.newPlan.amount, currency)}</span>
            </div>

            {preview.proratedCredit > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Credit for unused monthly time
                </span>
                <span className="text-success font-medium">
                  -{formatAmount(preview.proratedCredit, currency)}
                </span>
              </div>
            )}

            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Amount due today</span>
              <span>{formatAmount(preview.amountDue, currency)}</span>
            </div>
          </div>
        </div>

        {/* Savings + next renewal */}
        <div className="rounded-xl bg-secondary-soft border border-secondary/20 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-secondary mt-0.5 shrink-0" />
            <div className="space-y-1">
              {preview.annualSavings > 0 && (
                <p className="text-sm font-semibold text-foreground">
                  You save {formatAmount(preview.annualSavings, currency)} per
                  year
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Next renewal: {formatDate(preview.nextRenewalDate)}. A
                confirmation email will be sent to your account email.
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="outline" onClick={onCancel} disabled={isUpgrading}>
          Cancel
        </Button>
        <Button onClick={onUpgrade} disabled={isUpgrading}>
          {isUpgrading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Upgrading...
            </>
          ) : (
            <>Confirm Upgrade — {formatAmount(preview.amountDue, currency)}</>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}

function SuccessView({
  preview,
  onClose,
}: {
  preview: UpgradePreview | null;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-6">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-soft">
        <CheckCircle2 className="h-8 w-8 text-success" />
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-foreground">
          Plan upgraded successfully!
        </h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          You&apos;re now on the Annual plan.
          {preview && (
            <> Your next renewal is on {formatDate(preview.nextRenewalDate)}.</>
          )}
        </p>
        <p className="text-sm text-muted-foreground">
          A confirmation email has been sent to your account.
        </p>
      </div>
      <Button onClick={onClose} className="mt-2">
        Done
      </Button>
    </div>
  );
}
